import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';

import { Box, Heading } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import BackButtonWithFormState from '@rover/react-lib/src/pages/provider-profile/components/BackButtonWithFormState';
import { useApiProviderProfileModuleCreate } from '@rover/rsdk/src/apiClient/latest/api-provider-profile-module-create';
import {
  getApiProviderProfileModuleRetrieveQueryKey,
  useApiProviderProfileModuleRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-provider-profile-module-retrieve';
import type { ExperienceModule } from '@rover/rsdk/src/apiClient/latest/model/experienceModule';
import type { ExperienceModuleProfilePartial } from '@rover/rsdk/src/apiClient/latest/model/experienceModuleProfilePartial';
import type { PhotosModule } from '@rover/rsdk/src/apiClient/latest/model/photosModule';
import emitAnalyticsEvent from '@rover/rsdk/src/modules/Analytics/emitAnalyticsEvent';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import { ProviderProfileInfoUpdated } from '../analyticsEvents';
import { MODULE_KEYS } from '../constants/moduleKeys';
import { ReturnQueryParam } from '../types/ServiceForm';

import DogCareDetailsForm from './components/DogCareDetailsForm';
import PetCareDetailsShimmer from './components/PetCareDetailsShimmer';
import useBackRouteParams from './hooks/useBackRouteParams';
import { type DogCareDetailsFormData } from './schema';

const DogCareDetailsPage: React.FC = () => {
  const { t } = useLingui();
  const { backRoute, onSuccessRedirect, isFullPageRedirect } = useBackRouteParams(
    ReturnQueryParam.ExperienceAdded
  );
  const queryClient = useQueryClient();
  const { data: currentUser, providerState = '' } = useCurrentUser({});
  const userOpk = currentUser && 'currentUser' in currentUser ? currentUser.currentUser.opk : '';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formIsDirty, setFormIsDirty] = useState(false);

  // fetch experience module data
  const { data: experienceData, isLoading: experienceIsLoading } =
    useApiProviderProfileModuleRetrieve(MODULE_KEYS.EXPERIENCE);

  // Fetch photos module data
  const { data: photosData, isLoading: photosIsLoading } = useApiProviderProfileModuleRetrieve(
    MODULE_KEYS.PHOTOS_V2
  );

  // Use the generic ModuleAPIView with 'experience-profile' slug for partial submissions
  const { mutate: mutateExperienceProfileCreate } = useApiProviderProfileModuleCreate({
    mutation: {
      onMutate: () => setIsSubmitting(true),
      onSuccess: async (response) => {
        // Update ExperienceModule cache with the response
        queryClient.setQueryData(
          getApiProviderProfileModuleRetrieveQueryKey(MODULE_KEYS.EXPERIENCE),
          {
            ...experienceData,
            data: {
              ...experienceData?.data,
              ...response.data,
            },
          }
        );
        const event = new ProviderProfileInfoUpdated({
          provider_status: providerState,
          module: MODULE_KEYS.EXPERIENCE_PROFILE,
        });

        // emitAnalyticsEvent defers its dataLayer push, so it would be lost when the
        // redirect below unloads the page. Store it and let the next load replay it.
        if (isFullPageRedirect) {
          event.fireCrossPageEvent();
        } else {
          emitAnalyticsEvent(event);
        }
        setIsSubmitting(false);
        onSuccessRedirect();
      },
      onError: () => setIsSubmitting(false),
    },
  });

  const experienceModule = experienceData?.data as ExperienceModule | undefined;
  const photosModule = photosData?.data as PhotosModule | undefined;

  const handleSubmit = (values: DogCareDetailsFormData): void => {
    // Prepare partial data for ExperienceModuleProfile
    const data: ExperienceModuleProfilePartial = {
      headline: values.headline,
      experience: values.experience,
      yearsOfExperience: values.yearsOfExperience,
    };

    // Partial update ExperienceModule via ExperienceProfileModule endpoint
    mutateExperienceProfileCreate({
      moduleSlug: MODULE_KEYS.EXPERIENCE_PROFILE,
      data,
    });
  };

  return (
    <>
      <Helmet>
        <title>{t`Experience`}</title>
      </Helmet>

      <Section maxWidth={['100%', '640px']} data-qa-id="pet-care-details-page" py="6x">
        <BackButtonWithFormState href={backRoute} skipModal={!formIsDirty} />

        <Box py="8x">
          <Heading size="400" mb="6x">
            {t`Experience`}
          </Heading>
          {experienceIsLoading || photosIsLoading ? (
            <PetCareDetailsShimmer />
          ) : (
            <DogCareDetailsForm
              experienceModule={experienceModule}
              photosModule={photosModule}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              userOpk={userOpk}
              photosData={photosData}
              queryClient={queryClient}
              setFormIsDirty={setFormIsDirty}
            />
          )}
        </Box>
      </Section>
    </>
  );
};

export default DogCareDetailsPage;
